import type { RigidBody } from "@dimforge/rapier3d-compat";
import type { Size } from "./materials";

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}
export interface Quat extends Vec3 {
  w: number;
}
interface Aabb {
  lowerBound: Vec3;
  upperBound: Vec3;
}

/** What the game and the scoring read about a block once per frame. */
export interface BodyState {
  size: Size;
  born: number; // sim time when it was placed
  pos: Vec3;
  quat: Quat;
  aabb: Aabb;
  speed: number;
  spin: number;
  asleep: boolean;
  settled?: boolean;
  landed?: boolean;
}

/**
 * Half sizes of the axis-aligned bounding box around a rotated box: each
 * world axis gets the sum of the half sizes projected onto it, that is
 * |R(q)| · (size / 2) row by row.
 */
export function halfExtents(q: Quat, size: Size): Vec3 {
  const { x, y, z, w } = q;
  const hx = size[0] / 2,
    hy = size[1] / 2,
    hz = size[2] / 2;
  return {
    x:
      Math.abs(1 - 2 * (y * y + z * z)) * hx +
      Math.abs(2 * (x * y - w * z)) * hy +
      Math.abs(2 * (x * z + w * y)) * hz,
    y:
      Math.abs(2 * (x * y + w * z)) * hx +
      Math.abs(1 - 2 * (x * x + z * z)) * hy +
      Math.abs(2 * (y * z - w * x)) * hz,
    z:
      Math.abs(2 * (x * z - w * y)) * hx +
      Math.abs(2 * (y * z + w * x)) * hy +
      Math.abs(1 - 2 * (x * x + y * y)) * hz,
  };
}

/**
 * Rapier returns fresh objects on every query, so each block's state is read
 * once per frame into plain fields: position, rotation, bounding box, motion.
 * Fields are written one by one, so Three.js vectors work as `pos` and `quat`.
 */
export function readBody(body: RigidBody, out: BodyState): void {
  const t = body.translation(),
    r = body.rotation();
  out.pos.x = t.x;
  out.pos.y = t.y;
  out.pos.z = t.z;
  out.quat.x = r.x;
  out.quat.y = r.y;
  out.quat.z = r.z;
  out.quat.w = r.w;
  const e = halfExtents(r, out.size);
  Object.assign(out.aabb.lowerBound, { x: t.x - e.x, y: t.y - e.y, z: t.z - e.z });
  Object.assign(out.aabb.upperBound, { x: t.x + e.x, y: t.y + e.y, z: t.z + e.z });
  const v = body.linvel(),
    w = body.angvel();
  out.speed = Math.hypot(v.x, v.y, v.z);
  out.spin = Math.hypot(w.x, w.y, w.z);
  out.asleep = body.isSleeping();
}

/** Asleep, or moving and turning slowly enough to count as resting. */
export function isCalm(s: Pick<BodyState, "asleep" | "speed" | "spin">): boolean {
  return s.asleep || (s.speed < 0.15 && s.spin < 0.3);
}

/**
 * Calm and at least 0.3 s old: a held Drop button drops the next block now,
 * rather than waiting the 0.8 s a block takes to count as settled. Right after
 * a drop a block hasn't picked up speed yet, which the age rules out.
 */
export function touchedDown(b: BodyState, simTime: number): boolean {
  return simTime - b.born >= 0.3 && isCalm(b);
}

/* =====================================================================
   SCORING: tower height counts only settled blocks resting above the pedestal
   ===================================================================== */
export function measure(
  blocks: readonly BodyState[],
  simTime: number,
  held: BodyState | null,
  fx: number,
  fz: number,
): { settledTop: number; localTop: number } {
  let settledTop = 0,
    localTop = 0;
  for (const b of blocks) {
    if (b.pos.y < -1.5) continue; // fallen off the base
    const top = b.aabb.upperBound.y;
    b.settled = isCalm(b) && simTime - b.born > 0.8 && b !== held; // a held block never counts
    if (b.settled) b.landed = true; // stays true once the block has come to rest
    if (b.settled) settledTop = Math.max(settledTop, top);
    if (b.settled && Math.hypot(b.pos.x - fx, b.pos.z - fz) < 6) localTop = Math.max(localTop, top);
  }
  return { settledTop, localTop };
}
